interface Message<T> {
  id?: string;
  replyTo?: string;
  senderType: string;
  eventName: string;
  payload: T;
}

type MessageResolver<R> = {
  resolve: (data: R | PromiseLike<R>) => void;
  reject: (error: string | Error) => void;
  timeout: NodeJS.Timeout;
};

// Service for broadcasting messages across different contexts (e.g., tabs, windows)
export class BroadcastService {
  static instance: BroadcastService | null = null;
  private broadcastChannel: BroadcastChannel;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private pendingResponses = new Map<string, MessageResolver<any>>();

  constructor(channelName: string) {
    this.broadcastChannel = new BroadcastChannel(channelName);
    this.setupListeners();
  }

  // Method to get the singleton instance of the service
  static getInstance(channelName: string): BroadcastService {
    if (!BroadcastService.instance) {
      BroadcastService.instance = new BroadcastService(channelName);
    }

    return BroadcastService.instance;
  }

  // Set up listeners for incoming messages
  private setupListeners(): void {
    this.broadcastChannel.onmessage = <T>(event: MessageEvent<Message<T>>) => {
      const message = event.data;

      // Handle response to a previous request
      if (message.replyTo && this.pendingResponses.has(message.replyTo)) {
        const resolver = this.pendingResponses.get(message.replyTo)!;
        clearTimeout(resolver.timeout);
        resolver.resolve(message.payload);
        this.pendingResponses.delete(message.replyTo);
        return;
      }

      // Dispatch a custom event for listeners `window.addEventListener(...)`
      const customEvent = new CustomEvent(message.eventName, {
        detail: {
          payload: message.payload,
          reply: (responsePayload: unknown) => {
            if (message.id) {
              this.sendInternal({
                replyTo: message.id,
                senderType: message.senderType,
                eventName: message.eventName,
                payload: responsePayload
              });
            }
          }
        }
      });

      // Dispatch the event to the window
      window.dispatchEvent(customEvent);

      // Handle errors in receiving messages
      console.log(`[${message.senderType}] Received message:`, message.payload);
    };

    this.broadcastChannel.onmessageerror = (event) => {
      console.error('Error receiving message:', event);
    };
  }

  // Send a message without expecting a response
  sendMessageWithoutResponse<T>(senderType: string, eventName: string, payload: T): void {
    this.sendInternal({ senderType, eventName, payload });
  }

  // Send a message and expect a response within a specified timeout
  sendMessageWithResponse<T, R>(senderType: string, eventName: string, payload: T, timeoutMs = 5000): Promise<R> {
    const id = crypto.randomUUID();

    return new Promise<R>((resolve, reject) => {
      const timeout = setTimeout(() => {
        // Remove the resolver from the map on timeout
        this.pendingResponses.delete(id);
        reject(new Error(`Timeout waiting for response to event "${eventName}"`));
      }, timeoutMs);

      this.pendingResponses.set(id, { resolve, reject, timeout });

      this.sendInternal({ senderType, eventName, payload, id });
    });
  }

  // Internal method to send a message
  private sendInternal<T>(msg: Partial<Message<T>>) {
    const message: Message<T> = {
      id: msg.id,
      replyTo: msg.replyTo,
      senderType: msg.senderType!,
      eventName: msg.eventName!,
      payload: msg.payload!
    };

    this.broadcastChannel.postMessage(message);
    console.log(`[${msg.senderType}] Sent message:`, message);
  }
}
